import { useRef, useMemo, useState, useCallback } from 'react';
import { performPostRequest } from '@atlassian/jira-fetch';
import { useActiveJql } from '@atlassian/jira-issue-navigator';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters';
import type { IssueNavigatorPluggableItemsQuery } from './__generated_apollo__/IssueNavigatorPluggableItemsQuery';
import { ISSUE_NAVIGATOR_PLUGGABLE_ITEMS_QUERY } from './gql';

export type PluggableItems = {
	data: IssueNavigatorPluggableItemsQuery | undefined | null;
	loading: boolean;
	error: boolean;
};

type PluggableItemsFetcher = (forceRefetch?: boolean) => void;

export const pluggableItemsInitialState: PluggableItems = {
	data: undefined,
	loading: false,
	error: false,
};

export const usePluggableItemsQuery = (): [PluggableItemsFetcher, PluggableItems] => {
	const [pluggableItems, setPluggableItems] = useState<PluggableItems>({
		...pluggableItemsInitialState,
	});

	const { jql, filterId } = useActiveJql();

	const key = !jql && isFilterId(filterId) ? filterId : jql;

	const currentKeyRef = useRef(key);
	const lastKeyRef = useRef<unknown>(null);

	const fetchPluggableItems: PluggableItemsFetcher = useCallback((forceRefetch = false) => {
		if (
			forceRefetch ||
			lastKeyRef.current == null ||
			lastKeyRef.current !== currentKeyRef.current
		) {
			setPluggableItems({
				data: undefined,
				loading: true,
				error: false,
			});

			performPostRequest('/rest/gira/1/?operation=IssueNavigatorPluggableItemsQuery', {
				body: JSON.stringify({
					query: ISSUE_NAVIGATOR_PLUGGABLE_ITEMS_QUERY,
					variables: { first: 100 },
				}),
			})
				.then((response) => {
					if (response.errors) {
						setPluggableItems({
							data: null,
							loading: false,
							error: true,
						});
					} else {
						setPluggableItems({
							data: response.data,
							loading: false,
							error: false,
						});
					}
				})
				.catch(() => {
					setPluggableItems({
						data: null,
						loading: false,
						error: true,
					});
				});

			lastKeyRef.current = currentKeyRef.current;
		}
	}, []);

	// Sync this to avoid recreating the callback and mitigating rerender
	currentKeyRef.current = key;

	return useMemo(
		() => [fetchPluggableItems, pluggableItems],
		[pluggableItems, fetchPluggableItems],
	);
};
